/**
 * Base Element Renderer Interface
 */

import type { CSSProperties, ReactNode } from 'react';
import type { TemplateElement } from '../../../types/template.types';

export interface ElementRenderContext {
  element: TemplateElement;
  globalFontFamily: string;
  globalFontSize: number;
  globalFontColor: string;
  isStretched?: { horizontal: boolean; vertical: boolean };
  maxElementHeight?: number;
  isSelected: boolean;
  baseStyle: CSSProperties;
  elementStyle: CSSProperties;
  shouldStretchHorizontal: boolean;
  shouldStretchVertical: boolean;
  onClick: (e: React.MouseEvent) => void;
}

export interface ElementTypeRenderer {
  /**
   * Render the element content
   */
  render(context: ElementRenderContext): ReactNode;
  
  /**
   * Optional: Check if this renderer can handle the element type
   */
  canRender?(elementType: string): boolean;
}

export abstract class BaseElementRenderer implements ElementTypeRenderer {
  abstract render(context: ElementRenderContext): ReactNode;
  
  /**
   * Helper: Create placeholder display with colored background
   */
  protected renderPlaceholder(
    context: ElementRenderContext,
    placeholderText: string,
    bgColor: string,
    borderColor: string,
    textColor: string = 'inherit'
  ): ReactNode {
    return (
      <div 
        style={{
          ...context.elementStyle,
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          borderRadius: '4px',
          color: textColor,
        }} 
        className="whitespace-pre-wrap" 
        onClick={context.onClick}
      >
        <span style={{ color: textColor }}>{placeholderText}</span>
      </div>
    );
  }
  
  /**
   * Helper: Create editable text display
   */
  protected renderEditableText(
    context: ElementRenderContext,
    content: string,
    placeholder: string = 'Text eingeben...'
  ): ReactNode {
    return (
      <div 
        style={context.elementStyle} 
        className="whitespace-pre-wrap" 
        onClick={context.onClick}
      >
        {content || placeholder}
      </div>
    );
  }
}
